'use client';

import { Button } from 'reactstrap';
import { BRAND_NAME } from 'src/shared/constants/brand';

interface MaintenanceModePageProps {
  /** True while a recovery probe is in flight, so the action can show progress. */
  isChecking?: boolean;
  onCheckNow?: () => void;
}

/**
 * The maintenance window itself. Presentational — the polling and the redirect
 * live in {@link useMaintenanceModePoll}, so this can be rendered in isolation
 * from the style guide or a test.
 */
const MaintenanceModePage = ({ isChecking = false, onCheckNow }: MaintenanceModePageProps) => (
  <div
    data-testid="maintenance-mode-page"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="maintenance-mode-title"
    aria-describedby="maintenance-mode-description"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'var(--fill-white)',
    }}
  >
    <div className="nothing-card-xl">
      <div className="nothing-card-xl--icon">
        <svg
          width="110"
          height="108"
          viewBox="0 0 110 108"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <rect width="110" height="108" rx="54" fill="#FFF4EC" />
          <path
            d="M55 32.5c-12.426 0-22.5 10.074-22.5 22.5S42.574 77.5 55 77.5 77.5 67.426 77.5 55 67.426 32.5 55 32.5Zm0 42.031c-10.786 0-19.531-8.745-19.531-19.531S44.214 35.469 55 35.469 74.531 44.214 74.531 55 65.786 74.531 55 74.531Z"
            fill="#FFA064"
          />
          <path
            d="M56.484 43.633a1.484 1.484 0 0 0-2.968 0V55c0 .394.156.771.434 1.05l7.148 7.148a1.484 1.484 0 0 0 2.1-2.1l-6.714-6.713V43.633Z"
            fill="#FFA064"
          />
        </svg>
      </div>
      <h4 className="h3" id="maintenance-mode-title">
        {BRAND_NAME} is under maintenance
      </h4>
      <p className="p-3 text-center" id="maintenance-mode-description">
        We&apos;re making some improvements and will be back shortly. This page will refresh
        automatically once we&apos;re done.
      </p>
      {onCheckNow ? (
        <div className="nothing-action">
          <Button
            type="button"
            data-testid="maintenance-mode-check-now"
            disabled={isChecking}
            onClick={onCheckNow}
            className="ls-button ls-button-secondary has-secondary"
          >
            {isChecking ? 'Checking…' : 'Check now'}
          </Button>
        </div>
      ) : null}
    </div>
  </div>
);

export default MaintenanceModePage;
